import { useAbac } from "utils/abac";
import mainSidebarItems from "components/sidebar/sidebar-links/main-sidebar";
import { systemSettingGlobalSettingLinks, systemSettingAdminSettingLinks } from "components/sidebar/sidebar-links/system-settings-sidebar";
import { Links } from "components/sidebar/types";

export interface UseFirstSidebarPage {
  systemSettings?: boolean;
}

/**
    @description - Custom hook to identity the first page a user has permission to on the regular sidebar or on the system settings sidebar
    @params - systemSettings - boolean - whether to check the system settings sidebar or not (optional - not passing anything in will output regular sidebar first valid page)
    @returns - string - the route of the first valid page (ex. `/customer/companies`)
*/

const useFirstSidebarPage = ({ systemSettings }: UseFirstSidebarPage): string => {
  const { userHasPermissions } = useAbac();

  // either main sidebar links or all system settings links
  const links: Links = systemSettings ? { ...systemSettingGlobalSettingLinks, ...systemSettingAdminSettingLinks } : mainSidebarItems;

  let firstValidPageRoute: string = "";

  // loop through menus and pages and identify the first page a user has the needed permissions for
  for (const link in links) {
    const { type, route, permission, subPages } = links[link];

    if (type === "Page") {
      const hasPermission = userHasPermissions(permission);

      if (hasPermission) {
        firstValidPageRoute = `/${route}`;
        break;
      }
    } else {
      // if the link type is "Menu" - check the menu permission, then loop through all Menu's sub pages
      const hasPermission = userHasPermissions(permission);

      if (hasPermission) {
        for (const subPage of subPages) {
          const { route: subPageRoute, permission: subPagePermission } = subPage;

          const hasPagePermission = userHasPermissions(subPagePermission);

          if (hasPagePermission) {
            firstValidPageRoute = `/${route}/${subPageRoute}`;
            break;
          }
        }
      }
    }
  }

  systemSettings && (firstValidPageRoute = `/system-settings${firstValidPageRoute}`);
  return firstValidPageRoute;
};

export default useFirstSidebarPage;
